<template>
  <chart :options="graph" :auto-resize="true" ref="chart"></chart>
</template>

<script>
import ECharts from 'vue-echarts/components/ECharts';
import moment from 'moment';
import { each, has, get, isEmpty, sortBy } from 'lodash';
import { numberFormat } from 'utils';
import * as EVENT from 'constants/event';

import 'echarts/lib/chart/line';
import 'echarts/lib/chart/bar';

import 'echarts/lib/component/title';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/legend';
import 'echarts/lib/component/toolbox';
import 'echarts/lib/component/graphic';

import { CHART_COLORS } from 'constants/content-colors';

export default {
  props: {
    measurements: {
      required: true,
      default: null,
    },
    chartDetails: {
      type: Object,
      required: false,
      defalt: null,
    },
    measurementDetails: {
      type: Object,
      required: false,
      defalt: null,
    },
  },
  components: {
    chart: ECharts,
  },
  data() {
    return {
      labels: {
        text: `${this.$t(this.chartDetails.section)}: ${this.$t(this.chartDetails.chart)}`,
        subtext: `${this.measurementDetails.participant}, ${this.$t('label.time_period')}: ${moment(this.measurementDetails.date[0]).format('DD/MM/YYYY')} - ${moment(this.measurementDetails.date[1]).format('DD/MM/YYYY')}`,
        loading: `${this.$t('label.loading')}...`,
        saveAsImage: this.$t('label.save_as_image'),
        dataView: this.$t('label.data_view'),
        close: this.$t('label.close'),
        refresh: this.$t('label.refresh'),
        graphGeneratedByDietCreator: this.$t('label.graph_generated_by_diet_creator'),
        weight: this.$t('label.weight'),
        measurements: this.$t('label.measurements'),
        water: this.$t('label.water'),
        bodyFat: this.$t('label.body_fat'),
        optimalBodyFat: this.$t('label.optimal_body_fat'),
        bmi: this.$t('label.bmi'),
        bodyMassIndex: this.$t('label.body_mass_index'),
      },
      bmiColors: {
        undefined: '#000000',
        under_weight: 'rgba(134, 148, 206, 0.75)',
        healthy_weight: 'rgba(92, 186, 175, 0.75)',
        over_weight_i: 'rgba(209, 168, 100, 0.75)',
        over_weight_ii: 'rgba(168, 125, 54, 0.75)',
        obesity_i: 'rgba(228, 110, 100, 0.75)',
        obesity_ii: 'rgba(194, 70, 60, 0.75)',
        severely_obese: 'rgba(159, 38, 29, 0.75)',
        morbidly_obese: 'rgba(112, 0, 73, 0.75)',
      },
      weightUnit: null,
      graph: null,
    };
  },
  methods: {
    formatTooltip(params, ticket, callback) {
      setTimeout(() => {
        // console.log('params', params);
        let html = '';
        const sorted = sortBy(params, ['data.seq']);
        each(sorted, (param, index) => {
          if (index === 0) html += `${param.axisValue}<br/>`;
          // const percent = has(param, 'data.percent') ? ` (${numberFormat(param.data.percent, 2)}%)` : '';
          const unit = get(param, 'data.unit') || '';
          html += `${param.marker} ${param.seriesName}: ${numberFormat(param.data.value, 2)} ${unit}<br/>`;
        });
        callback(ticket, html);
      }, 200);
      return this.labels.loading;
    },
    formatLabel(measurement) {
      const timestamp = has(measurement, 'measurement_timestamp') ? measurement.measurement_timestamp : '';
      const date = moment(timestamp).format('DD/MM/YYYY');
      const time = moment(timestamp).format('HH:mm');
      return `${date}\n${time}`;
    },
    prepareData() {
      return new Promise((resolve) => {
        const series = [];
        const labels = [];

        const bmi = {
          type: 'bar',
          name: this.labels.bmi,
          barMaxWidth: 35,
          yAxisIndex: 1,
          data: [],
        };

        const weight = {
          name: this.labels.weight,
          type: 'line',
          smooth: true,
          yAxisIndex: 0,
          symbolSize: 6,
          data: [],
        };

        const bodyWaterWeight = {
          name: this.labels.water,
          type: 'line',
          smooth: true,
          yAxisIndex: 0,
          symbolSize: 6,
          data: [],
        };

        const bodyFatWeight = {
          name: this.labels.bodyFat,
          type: 'line',
          smooth: true,
          yAxisIndex: 0,
          symbolSize: 6,
          data: [],
        };

        const optimalBodyFatWeight = {
          name: this.labels.optimalBodyFat,
          type: 'line',
          smooth: true,
          yAxisIndex: 0,
          symbolSize: 6,
          data: [],
        };

        each(this.measurements, (measurement) => {
          const label = this.formatLabel(measurement);
          labels.push(label);

          const bmiValue = has(measurement, 'MeasurementTypeGroups.body_mass_index.Measurements.body_mass_index.value') ? measurement.MeasurementTypeGroups.body_mass_index.Measurements.body_mass_index.value : 0;
          const bmiCategory = has(measurement, 'MeasurementTypeGroups.body_mass_index.Measurements.body_mass_index_level.value') ? measurement.MeasurementTypeGroups.body_mass_index.Measurements.body_mass_index_level.value : null;
          const bmiBgColor = bmiCategory ? (has(this.bmiColors, bmiCategory) ? this.bmiColors[bmiCategory] : 'undefined') : 'undefined';
          bmi.data.push({
            name: this.labels.bmi,
            value: bmiValue,
            itemStyle: {
              normal: {
                color: bmiBgColor,
              },
            },
            seq: 5,
          });

          const bodyFatWeightValue = get(measurement, 'MeasurementTypeGroups.body_composition.Measurements.body_fat_weight.value') || 0;
          const bodyFatWeightUnit = get(measurement, 'MeasurementTypeGroups.body_composition.Measurements.body_fat_weight.expressed_in_unit') || '';
          const bodyFatWeightPercent = get(measurement, 'MeasurementTypeGroups.body_composition.Measurements.body_fat.value') || 0;
          bodyFatWeight.data.push({
            name: this.labels.fat,
            value: bodyFatWeightValue,
            unit: bodyFatWeightUnit,
            percent: bodyFatWeightPercent,
            seq: 2,
          });

          const bodyWaterValue = get(measurement, 'MeasurementTypeGroups.body_composition.Measurements.body_water_weight.value') || 0;
          const bodyWaterUnit = get(measurement, 'MeasurementTypeGroups.body_composition.Measurements.body_water_weight.expressed_in_unit') || '';
          const bodyWaterPercent = get(measurement, 'MeasurementTypeGroups.body_composition.Measurements.body_water.value') || '';
          bodyWaterWeight.data.push({
            name: this.labels.water,
            value: bodyWaterValue,
            unit: bodyWaterUnit,
            percent: bodyWaterPercent,
            seq: 3,
          });

          const optimalBodyFatValue = get(measurement, 'MeasurementTypeGroups.body_composition.Measurements.optimal_fat_weight.value') || 0;
          const optimalBodyFatUnit = get(measurement, 'MeasurementTypeGroups.body_composition.Measurements.optimal_fat_weight.expressed_in_unit') || '';
          optimalBodyFatWeight.data.push({
            name: this.labels.optimalBodyFat,
            value: optimalBodyFatValue,
            unit: optimalBodyFatUnit,
            seq: 4,
          });

          const weightValue = get(measurement, 'MeasurementTypeGroups.body_composition.Measurements.weight.value') || 0;
          const weightUnit = get(measurement, 'MeasurementTypeGroups.body_composition.Measurements.weight.expressed_in_unit') || '';
          // Set global scale unit
          this.weightUnit = weightUnit;
          weight.data.push({
            name: this.labels.weight,
            value: weightValue,
            unit: weightUnit,
            seq: 1,
          });
        });

        series.push(weight);
        series.push(bodyFatWeight);
        series.push(bodyWaterWeight);
        series.push(optimalBodyFatWeight);
        series.push(bmi);

        resolve({ series, labels });
      });
    },
    prepareGraph() {
      this.prepareData().then((data) => {
        const graph = {
          title: {
            text: this.labels.text,
            subtext: this.labels.subtext,
            subtextStyle: {
              color: '#000',
              fontSize: 13,
            },
            top: 10,
            left: '10%',
            padding: 0,
            itemGap: 5,
          },
          toolbox: {
            feature: {
              saveAsImage: {
                show: true,
                title: this.labels.saveAsImage,
              },
              dataView: {
                show: true,
                title: this.labels.dataView,
                lang: [this.labels.dataView, this.labels.close, this.labels.refresh],
                readOnly: true,
              },
            },
            top: 7,
            right: 15,
            itemGap: 15,
          },
          graphic: [
            {
              type: 'text',
              top: 'center',
              right: '20',
              rotation: Math.PI / 2,
              style: {
                text: this.labels.graphGeneratedByDietCreator,
                fill: '#afafaf',
                textVeticalAlign: 'middle',
              },
            },
          ],
          tooltip: {
            trigger: 'axis',
            formatter: this.formatTooltip,
          },
          legend: {
            align: 'right',
            top: 10,
            right: '10%',
            data: [
              { name: this.labels.weight },
              { name: this.labels.bodyFat },
              { name: this.labels.water },
              { name: this.labels.optimalBodyFat },
              { name: this.labels.bmi },
            ],
          },
          color: [
            CHART_COLORS.weight,
            CHART_COLORS.body_fat,
            CHART_COLORS.water,
            CHART_COLORS.optimal_body_fat,
            CHART_COLORS.bmi,
          ],
          xAxis: [
            {
              name: this.labels.measurements,
              nameLocation: 'middle',
              nameGap: 40,
              nameTextStyle: {
                fontWeight: 'bold',
              },
              type: 'category',
              boundaryGap: false,
              axisTick: {
                alignWithLabel: true,
              },
              axisLine: {
                onZero: false,
              },
              splitLine: {
                show: true,
                lineStyle: {
                  type: 'dashed',
                },
              },
              data: data.labels,
            },
          ],
          yAxis: [
            {
              type: 'value',
              name: this.labels.weight,
              nameLocation: 'middle',
              nameGap: 55,
              position: 'left',
              axisLine: {
                lineStyle: {
                  color: '#000',
                },
              },
              axisLabel: {
                formatter: `{value} ${this.weightUnit}`,
              },
              splitLine: {
                lineStyle: {
                  type: 'dashed',
                },
              },
            },
            {
              type: 'value',
              name: this.labels.bodyMassIndex,
              nameLocation: 'middle',
              nameGap: 65,
              position: 'right',
              axisLabel: {
                formatter: `{value} ${this.labels.bmi}`,
              },
            },
          ],
          series: data.series,
        };
        this.graph = graph;
      });
    },
  },
  mounted() {
    this.prepareGraph();
    const chart = this.$refs.chart;
    window.Eventus.$on(EVENT.LAYOUT_DEFAULT_SLIDE_MOVE, () => {
      console.log('now will resize happend');
      if (chart) chart.resize();
    });
  },
  watch: {
    measurements(newVal) {
      if (!isEmpty(newVal)) {
        this.prepareGraph();
      }
    },
  },
  beforeDestroy() {
    window.Eventus.$off(EVENT.LAYOUT_DEFAULT_SLIDE_MOVE);
  },
};
</script>

<style>
.echarts {
  width: 100% !important;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/professional/persons/participants/participant-evolution/dashboard/charts/chart-body-components.vue